import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import Image from "common/blocks/Image";
import InfoButton from "common/blocks/InfoButton";
import UIIcon from "common/blocks/UIIcon";
import useModalContext from "common/providers/modal/useModalContext";
import { valuationRangeModal, vehicleValuedModal } from "features/vehicleDetails/modals";
import { PartExchange } from "services/motorpoint/partExchange";
import { PartExchangeQuestionAnswersResponse } from "services/motorpoint/partExchangeQuestions";
import formatCurrency from "utils/formatCurrency";

import {
    HigherOfferContent,
    WHAT_IS_EQUITY_POPUP_NAME,
} from "../../WidgetsRow/components/PartExchangeWidget";
import styles from "./VehicleBadge.module.scss";

const cx = classNames.bind(styles);

export type VehicleBadgeProps = {
    imageUrl: string;
    info: string;
    higherOfferContent?: HigherOfferContent;
    partExQuestionsAnswersResponse?: PartExchangeQuestionAnswersResponse.AnswersResponse;
    changeVehicle?: () => void;
    appraisalExpiryDate?: Date;
};

const VehicleBadge = ({
    imageUrl,
    info,
    partExQuestionsAnswersResponse,
    higherOfferContent,
    changeVehicle,
    appraisalExpiryDate,
}: VehicleBadgeProps) => {
    const { usePopup, useSidebar } = useModalContext();
    const { openRegisteredPopup } = usePopup();
    const { openSidebar } = useSidebar();

    const now = Date.now();
    const difference = appraisalExpiryDate ? appraisalExpiryDate.getTime() - now : undefined;
    const totalDays = difference ? Math.floor(difference / (1000 * 3600 * 24)) : undefined;

    // todo: use dayjs for that.
    const yyyy = appraisalExpiryDate?.getFullYear().toString();
    let mm: string | number | undefined = appraisalExpiryDate
        ? appraisalExpiryDate.getMonth() + 1
        : undefined;
    let dd: string | number | undefined = appraisalExpiryDate?.getDate();
    if (dd && dd < 10) dd = "0" + dd.toString();
    if (mm && mm < 10) mm = "0" + mm.toString();

    const openValuationRangeSidebar = () =>
        openSidebar(
            valuationRangeModal({
                higherOfferSidePanel: higherOfferContent?.higherOfferSidePanel,
                partExchange: partExQuestionsAnswersResponse as PartExchange,
                previousSidebar: vehicleValuedModal(),
                closingModalGoesToPrevious: true,
            })
        );

    return (
        <div className={cx("vehicle")}>
            <div className={cx("image")}>
                <Image src={imageUrl} alt={info} layout="fill" />
            </div>
            <span className={cx("info")}>{info}</span>
            {changeVehicle && (
                <Button ariaLabel="Change vehicle" theme="textLink" onClick={changeVehicle}>
                    Change vehicle
                </Button>
            )}
            {!!partExQuestionsAnswersResponse && (
                <div className={cx("calculations")}>
                    <div className={cx("calculation")}>
                        <span className={cx("label")}>Equity</span>
                        <div className={cx("equityRow")}>
                            <span
                                className={cx("value")}
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(
                                        partExQuestionsAnswersResponse.Equity,
                                        2,
                                        true
                                    ),
                                }}
                            />
                            <InfoButton
                                onClick={() =>
                                    openRegisteredPopup(
                                        WHAT_IS_EQUITY_POPUP_NAME,
                                        {},
                                        {
                                            closingModalGoesToPrevious: true,
                                            previousModal: vehicleValuedModal,
                                        }
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className={cx("calculation")}>
                        <span className={cx("label")}>Value</span>
                        <span
                            className={cx("value")}
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(
                                    partExQuestionsAnswersResponse.Valuation,
                                    2,
                                    true
                                ),
                            }}
                        />
                    </div>
                </div>
            )}
            {!!partExQuestionsAnswersResponse?.ValuationRange?.MaximumValuation && (
                <div className={cx("offerMore")}>
                    <span className={cx("label")}>{higherOfferContent?.higherOfferLabel}</span>
                    <div className={cx("valueWrapper")}>
                        <span className={cx("upToLabel")}>up to</span>
                        <span
                            className={cx("upToValue")}
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(
                                    partExQuestionsAnswersResponse.ValuationRange.MaximumValuation,
                                    2,
                                    true
                                ),
                            }}
                        ></span>
                    </div>
                    <div className={cx("colourScale")} />
                    <div className={cx("linkButtonWrapper")}>
                        <Button
                            theme="textLink"
                            ariaLabel={higherOfferContent?.higherOfferButtonText}
                            onClick={openValuationRangeSidebar}
                        >
                            {higherOfferContent?.higherOfferButtonText}
                        </Button>
                    </div>
                </div>
            )}
            {!!appraisalExpiryDate && totalDays && totalDays > 0 && (
                <div className={cx("messageArea")}>
                    <p className={cx("valuationMessage")}>
                        Your valuation is valid for {totalDays} day{totalDays > 1 ? "s" : ""} until{" "}
                        {`${dd}/${mm}/${yyyy}`}
                    </p>
                    <div className={cx("phoneDetailsWrapper")}>
                        <div className={cx("infoIcon")}>
                            <UIIcon icon="Info" />
                        </div>
                        <span className={cx("phoneDetails")}>
                            If you had a better quote elsewhere, or wish to discuss your valuation
                            please get in touch with our team.
                        </span>
                        <div className={cx("phoneWrapper")}>
                            <div className={cx("phoneIcon")}>
                                <UIIcon icon="Tel" />
                            </div>
                            <DynamicPhoneNumber isLink={true} className={cx("phoneNumber")} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VehicleBadge;
